import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { queryOptions } from '@tanstack/react-query';

import { SiteLocale } from 'constants/i18n.constants';
import { accountRequester, datoRequester } from 'queries/requesters';
import { AppSyncGraphQLError } from 'queries/types';
import {
  GetSaudiRewardsRetroRedeemMatchingOrdersDocument,
  GetSaudiRewardsRetroRedeemUserDocument,
} from 'types/api/account.types';
import { PageEventsDocument } from 'types/api/dato-operations.types';

const SAUDI_REWARDS_RETRO_EARN_PREFIX = 'saudi-rewards-retro-redeem';

export const saudiRewardsRetroRedeemUserQuery = (
  variables: VariablesOf<typeof GetSaudiRewardsRetroRedeemUserDocument>,
) =>
  queryOptions<
    ResultOf<typeof GetSaudiRewardsRetroRedeemUserDocument>,
    AppSyncGraphQLError
  >({
    queryKey: [SAUDI_REWARDS_RETRO_EARN_PREFIX, 'user', variables],
    queryFn: accountRequester(
      GetSaudiRewardsRetroRedeemUserDocument,
      variables,
    ),
    retry: false,
  });

export const saudiRewardsRetroRedeemMatchingOrdersQuery = (variables: {
  locale: SiteLocale;
}) =>
  queryOptions({
    queryKey: [SAUDI_REWARDS_RETRO_EARN_PREFIX, 'orders'],
    retry: false,
    queryFn: async (ctx) => {
      const { saudiRewardsRetroRedeemMatchingOrders: orders } =
        await accountRequester(
          GetSaudiRewardsRetroRedeemMatchingOrdersDocument,
        )();

      const eventIds = [...new Set(orders.map((order) => order.eventId))];
      const timeslotIds = [
        ...new Set(orders.flatMap((order) => order.timeslotIds)),
      ];

      const { allPageEvents: events } = await datoRequester(
        PageEventsDocument,
        {
          locale: variables.locale,
          filter: {
            ticketsEventId: { in: eventIds },
            OR: [
              { ticketsTimeslotId: { isBlank: true } },
              { ticketsTimeslotId: { in: timeslotIds } },
            ],
          },
          first: 500,
          orderBy: 'startDatetime_ASC',
        },
      )(ctx);

      const enrichedOrders = orders.map((order) => {
        const matchedEvents = events.filter(
          (event) => event.ticketsEventId === order.eventId,
        );

        // If we only get back one event, then we know its the correct one, else also check the timeslot (recurring events)
        const event =
          matchedEvents?.length === 1
            ? matchedEvents[0]
            : matchedEvents.filter(
                (event) =>
                  !!event?.ticketsTimeslotId &&
                  order.timeslotIds.includes(event.ticketsTimeslotId),
              )[0];

        return {
          ...order,
          title: event?.title,
          cover: event.cover,
        };
      });

      return { orders: enrichedOrders ?? [] };
    },
  });
